'use client';

import { useEffect, useState } from 'react';

/**
 * Recover from a deploy that removed the chunks an open tab still asks for.
 *
 * Every deploy replaces the container, so the previous build's `_next/static`
 * files stop existing the moment traffic switches. A tab loaded before the
 * switch that navigates client-side across it fetches a chunk by its old hash,
 * gets a 404, and the failed load throws inside the segment being rendered —
 * the nearest `error.tsx` then shows an error screen for what is really a
 * stale page (seen 2026-09-14 on /aukcje right after a deploy). Next 15.5's
 * router falls back to a full navigation when the build id differs, but has no
 * handling at all for a chunk that fails to load.
 *
 * The boundaries call `useStaleChunkReload`: a chunk-load error becomes one
 * full navigation to the same URL, which loads the current build. The guard in
 * sessionStorage makes it one — a second failure within the window is shown as
 * the ordinary error, never a reload loop.
 */

const RELOAD_GUARD_KEY = 'pb:stale-chunk-reload';
const RELOAD_GUARD_WINDOW_MS = 60_000;

const CHUNK_LOAD_MESSAGES = [
  /Loading chunk [\w-]+ failed/i,
  /Loading CSS chunk [\w-]+ failed/i,
  // Turbopack's runtime (dev today; production once builds move to it).
  /Failed to load chunk/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

/** Webpack's own error class, or the messages browsers use for a failed dynamic import. */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;

  return CHUNK_LOAD_MESSAGES.some(pattern => pattern.test(error.message));
};

type TGuard = { url: string; at: number };

/** The last reload, `null` for none, `undefined` when storage cannot be read. */
const readGuard = (): TGuard | null | undefined => {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_GUARD_KEY);

    return raw ? (JSON.parse(raw) as TGuard) : null;
  } catch {
    return undefined;
  }
};

const writeGuard = (guard: TGuard): void => {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify(guard));
  } catch {
    // Unreachable in practice: `canReload` refused when storage could not be read.
  }
};

const currentUrl = (): string => window.location.pathname + window.location.search;

/**
 * Whether this URL may reload for a stale chunk: not within the window of the
 * last such reload. Without storage there is no guard, so no reload either —
 * a build genuinely missing a chunk must show the error, not reload forever.
 */
const canReload = (): boolean => {
  const guard = readGuard();

  if (guard === undefined) return false;
  if (guard === null) return true;

  return guard.url !== currentUrl() || Date.now() - guard.at > RELOAD_GUARD_WINDOW_MS;
};

/**
 * True while the page is being reloaded for a stale chunk — render nothing
 * then, so the error screen never flashes. False for every other error, and
 * for a chunk error that already reloaded this URL within the last minute.
 */
export const useStaleChunkReload = (error: unknown): boolean => {
  const [reloading] = useState<boolean>(
    () => typeof window !== 'undefined' && isChunkLoadError(error) && canReload()
  );

  useEffect(() => {
    if (!reloading) return;

    writeGuard({ url: currentUrl(), at: Date.now() });
    // A full navigation, not `reload()`: the router has already pushed the
    // target URL, and this loads it from the current build.
    window.location.assign(window.location.href);
  }, [reloading]);

  return reloading;
};
